import React, { lazy, Suspense, useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { withChalkTalkErrorBoundary } from '@components/ChalkTalkErrorBoundary/ChalkTalkErrorBoundary';
import { ReportsPlaceholder } from '../ReportHarness/styles';

const ReportsHarness = lazy(() =>
  import('../ReportHarness').then((module) => ({ default: module.default })),
);

interface LastScoreByItemByUserReportProps {
  teacherId: string;
  reportId: string;
  activityId: string;
  users: { id: string; name: string }[];
  configProperties?: {
    display_time_spent: boolean;
    display_item_numbers: boolean;
    scoring_type: 'partial' | 'dichotomous';
  };
  usersNotAttempted?: { id: string; name: string }[];
}

// Time in milliseconds to wait before injecting missing student rows
// Needed to ensure that the Learnosity report has fully rendered before we manipulate the DOM
const JS_INJECTION_TIMEOUT = 500;

const LastScoreByItemByUserReport = ({
  teacherId,
  reportId,
  activityId,
  users,
  configProperties = {
    display_time_spent: true,
    display_item_numbers: true,
    scoring_type: 'partial',
  },
  usersNotAttempted = [],
}: LastScoreByItemByUserReportProps) => {
  const [widget, setWidget] = useState<any>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const reportData = useMemo(
    () => [
      {
        id: reportId,
        type: 'lastscore-by-item-by-user',
        users,
        activity_id: activityId,
        user_id: teacherId,
        ...configProperties,
      },
    ],
    [activityId, configProperties, reportId, users, teacherId],
  );

  const renderReport = !!reportData[0]?.id && !!reportData[0]?.users?.length && !!teacherId;

  const dataListener = useCallback(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      if (!renderReport || usersNotAttempted.length === 0) {
        return;
      }

      intervalRef.current = setInterval(() => {
        const table = document.querySelector('.lastscore-by-item-by-user table.lrn-report-table');
        if (!table) {
          return;
        }
        const columns = table.querySelector('tbody tr')?.children.length || 1;

        usersNotAttempted.forEach((student) => {
          const exists = table.querySelector(`td.lrn-report-user[data-user_id="${student.id}"]`);
          if (exists) {
            return;
          }
          const row = document.createElement('tr');
          const tdName = document.createElement('td');
          tdName.style.whiteSpace = 'nowrap';
          tdName.tabIndex = 0;
          tdName.className = 'lrn-report-user';
          tdName.dataset.user_id = student.id;
          tdName.setAttribute('aria-label', '');
          tdName.textContent = student.name;
          row.appendChild(tdName);
          const tdNoAttempt = document.createElement('td');
          tdNoAttempt.colSpan = columns - 1;
          tdNoAttempt.textContent = 'No Attempt';
          row.appendChild(tdNoAttempt);
          table.querySelector('tbody')?.appendChild(row);
        });
      }, JS_INJECTION_TIMEOUT);
    }, [usersNotAttempted, renderReport]);

    useEffect(() => {
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, []);

  const callbacks = useMemo(() => ({ dataListener }), [dataListener]);

  if (!renderReport) {
    return (
      <ReportsPlaceholder>
        <span>No Reports Available!</span>
      </ReportsPlaceholder>
    );
  }
  return (
    <Suspense
      fallback={
        <ReportsPlaceholder>
          <span>Loading ...</span>
        </ReportsPlaceholder>
      }
    >
      <ReportsHarness
        key={reportId}
        widget={widget}
        userId={teacherId}
        setWidget={setWidget}
        reports={reportData}
        reportId={reportId}
        callbacks={callbacks}
      />
    </Suspense>
  );
};

const wrappedLastScoreByItemByUserReport = withChalkTalkErrorBoundary(
  LastScoreByItemByUserReport,
);
export {
  wrappedLastScoreByItemByUserReport as LastScoreByItemByUserReport,
  LastScoreByItemByUserReportProps,
};
